import { useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useUnitSystem, UnitToggle, gToDisplay } from "@/lib/units";
import { CURRENCIES, currencyInfo, fmtMoney } from "@/lib/currency";
import {
  Scale, Plus, Trash2, Copy, RotateCcw, Info, Calculator, DollarSign, PieChart, Save, FolderOpen, Loader2,
} from "lucide-react";

// Unit conversion table → grams
const UNIT_TO_G = {
  g: 1,
  kg: 1000,
  oz: 28.3495,
  lb: 453.592,
  // Volume approximations (water density baseline; users can override with grams)
  ml: 1,
  l: 1000,
  tsp: 5,
  tbsp: 15,
  cup: 240,
};
const UNIT_LABEL = {
  g: "g", kg: "kg", oz: "oz", lb: "lb",
  ml: "ml", l: "L", tsp: "tsp", tbsp: "tbsp", cup: "cup",
};
const UNIT_OPTIONS = Object.keys(UNIT_TO_G);

const DEFAULT_INGREDIENTS = [
  { id: 1, name: "Bread flour",     amount: 500, unit: "g", is_flour: true,  cost_per_kg: 2.50 },
  { id: 2, name: "Water",           amount: 350, unit: "g", is_flour: false, cost_per_kg: 0.00 },
  { id: 3, name: "Salt",            amount: 10,  unit: "g", is_flour: false, cost_per_kg: 1.00 },
  { id: 4, name: "Active starter",  amount: 100, unit: "g", is_flour: false, cost_per_kg: 0.00 },
];

function toGrams(amount, unit) {
  const n = parseFloat(amount);
  if (isNaN(n)) return 0;
  return n * (UNIT_TO_G[unit] || 1);
}

function fmt(n, decimals = 0) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
}

export default function RecipeScaler() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [system, setSystem] = useUnitSystem();
  const [currency, setCurrency] = useState("USD");
  const [recipeName, setRecipeName] = useState("Country Sourdough");
  const [ingredients, setIngredients] = useState(DEFAULT_INGREDIENTS);
  const [nextId, setNextId] = useState(5);
  const [servings, setServings] = useState(8);
  const [showCosts, setShowCosts] = useState(true);
  // Scaling mode
  const [scaleMode, setScaleMode] = useState("factor"); // 'factor' | 'flour' | 'servings'
  const [scaleFactor, setScaleFactor] = useState(1);
  const [targetFlourG, setTargetFlourG] = useState(1000);
  const [targetServings, setTargetServings] = useState(16);
  const [savedId, setSavedId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [params] = useSearchParams();

  // Load a recipe if ?load=<id> is in the URL
  useEffect(() => {
    const loadId = params.get("load");
    if (!loadId || !user) return;
    api.get(`/recipes/${loadId}`).then(({ data }) => {
      setSavedId(data.id);
      setRecipeName(data.name);
      setServings(data.servings || 1);
      const withIds = (data.ingredients || []).map((i, idx) => ({ ...i, id: idx + 1 }));
      setIngredients(withIds.length ? withIds : DEFAULT_INGREDIENTS);
      setNextId(withIds.length + 1);
      if (typeof data.show_costs === "boolean") setShowCosts(data.show_costs);
      toast.success(`Loaded "${data.name}"`);
    }).catch(() => toast.error("Failed to load recipe"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, params]);

  useEffect(() => {
    if (loading) return;
    if (!user || user === false) { nav("/login"); return; }
    if (user.currency) setCurrency(user.currency);
  }, [user, loading, nav]);

  const saveCurrency = async (code) => {
    setCurrency(code);
    try {
      const { api } = await import("@/lib/api");
      await api.patch("/auth/me", { currency: code });
      if (refresh) await refresh();
    } catch {}
  };

  // Compute the flour baseline for baker's percentages
  const flourWeight = useMemo(() => {
    const f = ingredients.find((i) => i.is_flour);
    return f ? toGrams(f.amount, f.unit) : 0;
  }, [ingredients]);

  // Total original weight
  const totalWeight = useMemo(
    () => ingredients.reduce((sum, i) => sum + toGrams(i.amount, i.unit), 0),
    [ingredients],
  );

  // Effective multiplier based on chosen scale mode
  const multiplier = useMemo(() => {
    if (scaleMode === "flour") {
      if (!flourWeight) return 1;
      return (parseFloat(targetFlourG) || 0) / flourWeight;
    }
    if (scaleMode === "servings") {
      if (!servings) return 1;
      return (parseFloat(targetServings) || 0) / (parseFloat(servings) || 1);
    }
    return parseFloat(scaleFactor) || 1;
  }, [scaleMode, scaleFactor, targetFlourG, targetServings, flourWeight, servings]);

  const scaled = useMemo(() => {
    // In US mode users enter cost per POUND; in metric mode cost per KG
    const G_PER_UNIT = system === "us" ? 453.5924 : 1000;
    return ingredients.map((i) => {
      const originalG = toGrams(i.amount, i.unit);
      const scaledG = originalG * multiplier;
      const bp = flourWeight ? (originalG / flourWeight) * 100 : 0;
      const costPerBatch = (scaledG / G_PER_UNIT) * (parseFloat(i.cost_per_kg) || 0);
      return { ...i, originalG, scaledG, bp, costPerBatch };
    });
  }, [ingredients, flourWeight, multiplier, system]);

  const scaledTotalWeight = totalWeight * multiplier;
  const scaledFlourWeight = flourWeight * multiplier;
  const scaledServings = (parseFloat(servings) || 0) * multiplier;
  const totalCost = scaled.reduce((s, i) => s + i.costPerBatch, 0);
  const costPerServing = scaledServings > 0 ? totalCost / scaledServings : 0;
  const totalHydration = useMemo(() => {
    // Hydration ≈ (all liquid + starter half-water) / flour
    if (!flourWeight) return 0;
    const liquidNames = /water|milk|buttermilk|cream|beer|juice|coffee|tea|starter|levain|yolk|egg/i;
    const liquidsG = ingredients
      .filter((i) => !i.is_flour && liquidNames.test(i.name))
      .reduce((s, i) => s + toGrams(i.amount, i.unit), 0);
    return (liquidsG / flourWeight) * 100;
  }, [ingredients, flourWeight]);

  const updateIng = (id, patch) => {
    setIngredients((prev) => prev.map((i) => (i.id === id ? { ...i, ...patch } : i)));
  };
  const setFlour = (id) => {
    setIngredients((prev) => prev.map((i) => ({ ...i, is_flour: i.id === id })));
  };
  const addIng = () => {
    setIngredients((prev) => [
      ...prev,
      { id: nextId, name: "", amount: 0, unit: "g", is_flour: false, cost_per_kg: 0 },
    ]);
    setNextId((n) => n + 1);
  };
  const removeIng = (id) => {
    setIngredients((prev) => prev.filter((i) => i.id !== id));
  };
  const resetAll = () => {
    if (!window.confirm("Reset to the default sourdough example?")) return;
    setSavedId(null);
    setRecipeName("Country Sourdough");
    setIngredients(DEFAULT_INGREDIENTS);
    setNextId(5);
    setServings(8);
    setScaleFactor(1);
    setTargetFlourG(1000);
    setTargetServings(16);
    setScaleMode("factor");
  };

  const saveRecipe = async () => {
    if (!recipeName.trim()) { toast.error("Give your recipe a name first"); return; }
    setSaving(true);
    const payload = {
      name: recipeName.trim(),
      servings: parseFloat(servings) || 1,
      ingredients: ingredients.map((i) => ({
        name: i.name || "",
        amount: parseFloat(i.amount) || 0,
        unit: i.unit || "g",
        is_flour: !!i.is_flour,
        cost_per_kg: parseFloat(i.cost_per_kg) || 0,
      })),
      show_costs: showCosts,
      notes: null,
    };
    try {
      if (savedId) {
        const { data } = await api.patch(`/recipes/${savedId}`, payload);
        toast.success("Recipe updated");
        setSavedId(data.id);
      } else {
        const { data } = await api.post("/recipes", payload);
        toast.success("Saved to My Recipes");
        setSavedId(data.id);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  const copyRecipe = () => {
    const lines = [];
    lines.push(recipeName + (multiplier !== 1 ? ` (scaled ${multiplier.toFixed(2)}×)` : ""));
    lines.push("");
    lines.push(`Yield: ${fmt(scaledServings, 1)} servings · Total: ${(() => { const d = gToDisplay(scaledTotalWeight, system); return d.value === null ? "—" : `${d.value.toFixed(d.unit === "lb" || d.unit === "kg" ? 2 : 0)}${d.unit}`; })()}`);
    if (flourWeight) lines.push(`Flour: ${(() => { const d = gToDisplay(scaledFlourWeight, system); return d.value === null ? "—" : `${d.value.toFixed(d.unit === "lb" || d.unit === "kg" ? 2 : 0)}${d.unit}`; })()} (baker's % baseline)`);
    lines.push("");
    for (const i of scaled) {
      const bp = flourWeight ? ` — ${i.bp.toFixed(1)}%` : "";
      const d = gToDisplay(i.scaledG, system);
      const dv = d.value === null ? "—" : d.value.toFixed(d.unit === "lb" || d.unit === "kg" ? 2 : 1);
      lines.push(`• ${i.name || "(unnamed)"}: ${dv}${d.unit}${bp}`);
    }
    if (showCosts && totalCost > 0) {
      lines.push("");
      lines.push(`Total cost: ${fmtMoney(totalCost, currency)} · Per serving: ${fmtMoney(costPerServing, currency)}`);
    }
    navigator.clipboard.writeText(lines.join("\n"));
    toast.success("Recipe copied to clipboard");
  };

  if (loading || !user) return null;

  return (
    <>
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 lg:py-10">
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#857163]">
              <Scale className="w-3.5 h-3.5" style={{ color: "#8EAC50" }} />
              <span>Baker's tool</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-[#3B2C24] mt-1">
              Advanced <span style={{ color: "#8EAC50" }}>Recipe</span> Scaler
            </h1>
            <p className="mt-2 text-[#857163] max-w-2xl">
              Scale any recipe with baker's percentages, batch cost, and per-serving cost. Enter your recipe once and instantly get any batch size — half batch, double batch, or a target flour weight.
            </p>
          </div>
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Link
              to="/my-recipes"
              className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="my-recipes-link"
            ><FolderOpen className="w-3.5 h-3.5" /> My Recipes</Link>
            <UnitToggle system={system} onChange={setSystem} testid="scaler-unit-toggle" />
            <select
              value={currency}
              onChange={(e) => saveCurrency(e.target.value)}
              className="cp-input !py-1.5 !text-xs !pr-7 max-w-[130px]"
              data-testid="currency-select"
              title="Currency"
            >
              {CURRENCIES.map((c) => (
                <option key={c.code} value={c.code}>{c.symbol} {c.code}</option>
              ))}
            </select>
            <button
              onClick={saveRecipe}
              disabled={saving}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-sm font-semibold transition hover:opacity-90 disabled:opacity-50"
              style={{ background: "#8EAC50" }}
              data-testid="save-recipe-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {savedId ? "Update recipe" : "Save recipe"}
            </button>
            <button
              onClick={resetAll}
              className="cp-btn-ghost text-sm inline-flex items-center gap-1.5"
              data-testid="reset-btn"
            ><RotateCcw className="w-3.5 h-3.5" /> Reset</button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[1fr_400px] gap-6">
          {/* Left: recipe input */}
          <div className="cp-card p-5 sm:p-6 space-y-4">
            <div>
              <label className="text-xs text-[#857163]">Recipe name</label>
              <input
                className="cp-input mt-1"
                value={recipeName}
                onChange={(e) => setRecipeName(e.target.value)}
                placeholder="e.g. Country Sourdough"
                data-testid="recipe-name-input"
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-[#857163]">Original yield (servings / loaves)</label>
                <input
                  type="number"
                  min={0}
                  step={0.5}
                  className="cp-input mt-1"
                  value={servings}
                  onChange={(e) => setServings(e.target.value)}
                  data-testid="servings-input"
                />
              </div>
              <label className="flex items-end gap-2 pb-1">
                <input
                  type="checkbox"
                  checked={showCosts}
                  onChange={(e) => setShowCosts(e.target.checked)}
                  className="w-4 h-4"
                  data-testid="show-costs-toggle"
                />
                <span className="text-sm text-[#3B2C24]">Track ingredient costs</span>
              </label>
            </div>

            {/* Ingredients table */}
            <div className="border border-[#EBDCD1] rounded-2xl overflow-hidden">
              <div className="bg-[#F9EFE6] grid grid-cols-[24px_1fr_90px_80px_60px_36px] sm:grid-cols-[24px_1fr_100px_90px_70px_100px_36px] gap-2 items-center px-3 py-2 text-[11px] uppercase tracking-wider text-[#857163] font-semibold">
                <div title="Mark as flour (baker's % baseline)">100%</div>
                <div>Ingredient</div>
                <div>Amount</div>
                <div>Unit</div>
                <div className="hidden sm:block">Baker's %</div>
                {showCosts && <div className="hidden sm:block">{currencyInfo(currency).symbol} / {system === "us" ? "lb" : "kg"}</div>}
                <div></div>
              </div>

              {ingredients.map((i) => {
                const bp = flourWeight ? (toGrams(i.amount, i.unit) / flourWeight) * 100 : 0;
                return (
                  <div
                    key={i.id}
                    className="grid grid-cols-[24px_1fr_90px_80px_60px_36px] sm:grid-cols-[24px_1fr_100px_90px_70px_100px_36px] gap-2 items-center px-3 py-2 border-t border-[#EBDCD1]"
                    data-testid={`ingredient-row-${i.id}`}
                  >
                    <input
                      type="radio"
                      checked={i.is_flour}
                      onChange={() => setFlour(i.id)}
                      className="w-4 h-4 accent-[#8EAC50]"
                      aria-label="Mark as flour baseline"
                      data-testid={`ing-flour-${i.id}`}
                    />
                    <input
                      className="cp-input !py-1.5 !text-sm"
                      value={i.name}
                      onChange={(e) => updateIng(i.id, { name: e.target.value })}
                      placeholder="Ingredient"
                      data-testid={`ing-name-${i.id}`}
                    />
                    <input
                      type="number"
                      step={0.1}
                      min={0}
                      className="cp-input !py-1.5 !text-sm"
                      value={i.amount}
                      onChange={(e) => updateIng(i.id, { amount: e.target.value })}
                      data-testid={`ing-amount-${i.id}`}
                    />
                    <select
                      className="cp-input !py-1.5 !text-sm !pr-6"
                      value={i.unit}
                      onChange={(e) => updateIng(i.id, { unit: e.target.value })}
                      data-testid={`ing-unit-${i.id}`}
                    >
                      {UNIT_OPTIONS.map((u) => <option key={u} value={u}>{UNIT_LABEL[u]}</option>)}
                    </select>
                    <div className="hidden sm:block text-xs text-[#3B2C24]">
                      {i.is_flour ? <span className="font-semibold" style={{ color: "#8EAC50" }}>100%</span> : `${bp.toFixed(1)}%`}
                    </div>
                    {showCosts && (
                      <input
                        type="number"
                        step={0.01}
                        min={0}
                        className="cp-input !py-1.5 !text-sm hidden sm:block"
                        value={i.cost_per_kg}
                        onChange={(e) => updateIng(i.id, { cost_per_kg: e.target.value })}
                        placeholder="0.00"
                        data-testid={`ing-cost-${i.id}`}
                      />
                    )}
                    <button
                      onClick={() => removeIng(i.id)}
                      className="w-8 h-8 grid place-items-center rounded-full hover:bg-red-50 text-red-500"
                      aria-label="Remove"
                      data-testid={`ing-remove-${i.id}`}
                    ><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                );
              })}

              <button
                onClick={addIng}
                className="w-full py-2.5 text-sm text-[#857163] hover:bg-[#F9EFE6] border-t border-[#EBDCD1] transition inline-flex items-center justify-center gap-1.5"
                data-testid="add-ingredient-btn"
              ><Plus className="w-3.5 h-3.5" /> Add ingredient</button>
            </div>

            <div className="text-[11px] text-[#857163] leading-relaxed flex items-start gap-1.5">
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              <span>
                Mark one ingredient as your flour baseline (radio button) — every other ingredient is measured as a percentage of that flour weight. Volume units (cups, tbsp, etc.) use water-density approximations; for accurate baker's math, weigh in grams.
              </span>
            </div>

            {/* Scale controls */}
            <div className="pt-3 border-t border-[#EBDCD1]">
              <div className="text-sm font-medium text-[#3B2C24] mb-2 inline-flex items-center gap-1.5">
                <Calculator className="w-4 h-4" style={{ color: "#8EAC50" }} /> Scale to
              </div>
              <div className="flex gap-1.5 flex-wrap mb-3">
                {[
                  { key: "factor", label: "By factor" },
                  { key: "flour", label: "Target flour weight" },
                  { key: "servings", label: "Target servings" },
                ].map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setScaleMode(m.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition ${
                      scaleMode === m.key
                        ? "bg-[#8EAC50] text-white border-[#8EAC50]"
                        : "bg-white border-[#EBDCD1] text-[#3B2C24] hover:bg-[#F9EFE6]"
                    }`}
                    data-testid={`scale-mode-${m.key}`}
                  >{m.label}</button>
                ))}
              </div>
              {scaleMode === "factor" && (
                <div>
                  <label className="text-xs text-[#857163]">Multiplier (e.g. 0.5 = half batch, 2 = double)</label>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <input
                      type="number"
                      step={0.1}
                      min={0.1}
                      className="cp-input max-w-[160px]"
                      value={scaleFactor}
                      onChange={(e) => setScaleFactor(e.target.value)}
                      data-testid="scale-factor-input"
                    />
                    {[0.5, 1, 2, 3].map((v) => (
                      <button
                        key={v}
                        onClick={() => setScaleFactor(v)}
                        className="px-3 py-1.5 rounded-full text-xs border border-[#EBDCD1] hover:bg-[#F9EFE6]"
                        data-testid={`scale-quick-${v}`}
                      >{v}×</button>
                    ))}
                  </div>
                </div>
              )}
              {scaleMode === "flour" && (
                <div>
                  <label className="text-xs text-[#857163]">Target flour weight (grams)</label>
                  <input
                    type="number"
                    min={0}
                    className="cp-input mt-1 max-w-[200px]"
                    value={targetFlourG}
                    onChange={(e) => setTargetFlourG(e.target.value)}
                    placeholder="1000"
                    data-testid="target-flour-input"
                  />
                  {!flourWeight && <div className="text-xs text-red-500 mt-1">Mark an ingredient as your flour baseline first.</div>}
                </div>
              )}
              {scaleMode === "servings" && (
                <div>
                  <label className="text-xs text-[#857163]">Target servings / loaves</label>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="cp-input mt-1 max-w-[200px]"
                    value={targetServings}
                    onChange={(e) => setTargetServings(e.target.value)}
                    placeholder="16"
                    data-testid="target-servings-input"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Right: results */}
          <div className="cp-card p-5 sm:p-6 sticky top-24 self-start" data-testid="scaler-results">
            <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
              <div>
                <div className="text-xs uppercase tracking-widest text-[#857163]">Scaled recipe</div>
                <h2 className="font-heading text-2xl text-[#3B2C24] leading-tight">{recipeName || "Untitled"}</h2>
              </div>
              <button
                onClick={copyRecipe}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold text-white hover:opacity-90 transition"
                style={{ background: "#8EAC50" }}
                data-testid="copy-recipe-btn"
              ><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <Metric label="Multiplier" value={`${multiplier.toFixed(2)}×`} />
              <Metric label="Yield" value={`${fmt(scaledServings, 1)}`} suffix="serv." />
              {(() => {
                const bw = gToDisplay(scaledTotalWeight, system);
                const fw = gToDisplay(scaledFlourWeight, system);
                const fmtD = (d) => d.value === null ? "—" : d.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: d.unit === "lb" || d.unit === "kg" ? 2 : 0 });
                return (
                  <>
                    <Metric label="Batch weight" value={fmtD(bw)} suffix={bw.unit} />
                    <Metric label="Flour weight" value={fmtD(fw)} suffix={fw.unit} />
                  </>
                );
              })()}
            </div>

            <div className="space-y-1.5 mb-4 max-h-[280px] overflow-y-auto pr-1">
              {scaled.map((i) => (
                <div key={i.id} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-sm py-1 border-b border-[#EBDCD1]/50">
                  <div className="text-[#3B2C24] truncate">
                    {i.is_flour && <span className="inline-block w-1.5 h-1.5 rounded-full mr-1.5 align-middle" style={{ background: "#8EAC50" }} />}
                    {i.name || <span className="text-[#857163] italic">Unnamed</span>}
                  </div>
                  <div className="font-mono text-sm font-semibold text-[#3B2C24] tabular-nums">
                    {(() => {
                      const d = gToDisplay(i.scaledG, system);
                      const dec = d.unit === "lb" || d.unit === "kg" ? 2 : 1;
                      return <>{d.value === null ? "—" : d.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: dec })}<span className="text-[10px] text-[#857163] ml-0.5">{d.unit}</span></>;
                    })()}
                  </div>
                  <div className="text-xs text-[#857163] tabular-nums w-12 text-right">
                    {i.is_flour ? <span className="font-semibold" style={{ color: "#8EAC50" }}>100%</span> : `${i.bp.toFixed(1)}%`}
                  </div>
                </div>
              ))}
            </div>

            {flourWeight > 0 && totalHydration > 0 && (
              <div className="mb-4 p-3 rounded-2xl bg-[#F9EFE6] flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-[#3B2C24]">
                  <PieChart className="w-4 h-4" style={{ color: "#8EAC50" }} />
                  <span>Hydration (estimated)</span>
                </div>
                <div className="font-heading text-xl font-bold text-[#3B2C24]">{totalHydration.toFixed(0)}%</div>
              </div>
            )}

            {showCosts && (
              <div className="pt-3 border-t border-[#EBDCD1]">
                <div className="text-sm font-medium text-[#3B2C24] mb-2 inline-flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" style={{ color: "#F4A83B" }} /> Cost
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Metric label="Total batch" value={fmtMoney(totalCost, currency)} big />
                  <Metric label="Per serving" value={fmtMoney(costPerServing, currency)} big />
                </div>
                {totalCost === 0 && (
                  <div className="text-[11px] text-[#857163] mt-2">Add "{currencyInfo(currency).symbol}/{system === "us" ? "lb" : "kg"}" values for each ingredient to see cost.</div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </>
  );
}

function Metric({ label, value, suffix, big }) {
  return (
    <div className="p-3 rounded-2xl border border-[#EBDCD1]">
      <div className="text-[10px] uppercase tracking-widest text-[#857163]">{label}</div>
      <div className={`font-heading font-bold text-[#3B2C24] mt-0.5 tabular-nums ${big ? "text-2xl" : "text-lg"}`}>
        {value}{suffix && <span className="text-xs text-[#857163] ml-1 font-normal">{suffix}</span>}
      </div>
    </div>
  );
}
